// components/strangerDetection/components/switchStrangerModal.js
import React, { useState, useEffect } from 'react';
import Modal from 'components/ui/modal';
import Button from 'components/ui/button';
import Input from 'components/ui/input';
import Image from '../../../components/appImage';
import Icon from '../../../components/appIcon';
import strangerApi from 'services/api/strangerApi';
const BACKEND_URL = import.meta.env.VITE_API_URL;

const SwitchStrangerModal = ({ 
  stranger, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadEmployees();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = employees.filter(emp => 
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(employees);
    }
  }, [searchTerm, employees]);

  const loadEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await strangerApi.getEmployeesForSwitch();
      setEmployees(response.data.employees || []);
      setFilteredEmployees(response.data.employees || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      // Optionally show an alert or notification that no employee is selected
      return;
    }

    setIsSubmitting(true);
    try {
      await onSuccess({
        stranger_id: stranger.originalData ? stranger.originalData.id : stranger.id,
        target_employee_id: selectedEmployee.id
      });
      onClose();
    } catch (error) {
      console.error("Error switching stranger:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle image URL - check if it's already a full URL
  const strangerImageUrl = stranger?.imageUrl?.startsWith('http')
    ? stranger.imageUrl
    : `${BACKEND_URL}${stranger?.imageUrl}`;

  // Handle dropdown change
  const handleEmployeeChange = (e) => {
    const employeeId = parseInt(e.target.value);
    const employee = filteredEmployees.find(emp => emp.id === employeeId);
    setSelectedEmployee(employee || null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Switch Stranger to Employee">
      <div className="space-y-4">
        {/* Stranger Information */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h3 className="font-medium text-slate-800 mb-2">Stranger Information</h3>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-slate-200 rounded-full overflow-hidden">
              <Image
                src={strangerImageUrl}
                alt="Stranger"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-medium">{stranger?.first_name} {stranger?.last_name}</p>
              <p className="text-sm text-slate-500">Detected at: {stranger?.location}</p>
              <p className="text-sm text-slate-500">Time: {stranger?.detectedAt?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Employee Selection Dropdown */}
        <div>
          <h3 className="font-medium text-slate-800 mb-2">Select Employee</h3>
          <Input
            placeholder="Search by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-4"
          />
          
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <select
              value={selectedEmployee?.id || ''}
              onChange={handleEmployeeChange}
              className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="" disabled>
                {filteredEmployees.length > 0 ? 'Select an employee...' : 'No employees found'}
              </option>
              {filteredEmployees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} ({employee.employee_id}) - {employee.department}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Selected Employee Preview (Optional but good UX) */}
        {selectedEmployee && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">Selected Employee</h4>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full overflow-hidden">
                {selectedEmployee.image && (
                  <Image
                    src={selectedEmployee.image.startsWith('http') ? selectedEmployee.image : `${BACKEND_URL}${selectedEmployee.image}`}
                    alt={selectedEmployee.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div>
                <p className="font-medium text-blue-900">{selectedEmployee.name}</p>
                <p className="text-sm text-blue-700">{selectedEmployee.employee_id} • {selectedEmployee.department}</p>
                <p className="text-sm text-blue-700">{selectedEmployee.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleSubmit}
            disabled={!selectedEmployee || isSubmitting}
          >
            {isSubmitting ? 'Switching...' : 'Switch & Mark Attendance'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SwitchStrangerModal;